import { Button, Link, Text, useDisclosure } from '@ritualco/jutsu'
import { AddIcon } from '@chakra-ui/icons'
import { EditableSection } from 'components/EditableSection'
import { FullWidthPage } from 'components/PageTemplates'
import { ConnectionsTable } from 'components/Tables/ConnectionsTable'
import { ConnectionAccessCodeModal } from 'components/Modals/ConnectionAccessCodeModal'
import { Pagination } from 'components/Pagination'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'
import {
  useAcceptExternalWorkspaceConnectionCode,
  useDeleteExternalWorkspaceConnection,
  useExternalConnectionsQuery,
} from 'api/endpoints/connections'
import { useState } from 'react'
import Loading from 'components/Loading'
import { INITIAL_ROWS_PER_PAGE } from 'config'

type ConnectionsProps = {
  organizationId: string
  workspaceId: string
  connectionId?: string
}

const Connections = ({
  workspaceId,
  organizationId,
  connectionId,
}: ConnectionsProps) => {
  const { formatMessage } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: !!connectionId,
  })
  const { addMessage } = useMessage()
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginationTokenOption, setPaginationTokenOption] = useState({})
  const { status, data } = useExternalConnectionsQuery({
    workspaceId,
    organizationId,
    limit: rowsPerPage,
    ...paginationTokenOption,
  })
  const accept = useAcceptExternalWorkspaceConnectionCode()
  const remove = useDeleteExternalWorkspaceConnection()

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  const handleRemove = (connectionId: string) => {
    remove.mutate(
      { organizationId, workspaceId, connectionId },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.connections.removeSuccess',
            }),
            isClosable: false,
          })
        },
      }
    )
  }

  return (
    <FullWidthPage
      heading={formatMessage({ id: 'pmportal.connections.title' })}
      subheading={
        <Text color="grayscale.secondary" fontSize="small">
          {formatMessage(
            { id: 'pmportal.connections.subtitle' },
            {
              link: text => (
                // TODO: update this link
                <Link href="https://ritual.co/faq" isExternal>
                  {text}
                </Link>
              ),
            }
          )}
        </Text>
      }
    >
      <EditableSection
        title={formatMessage({
          id: 'pmportal.connections.connectionsTableTitle',
        })}
        action={
          <Button
            colorScheme="transparent"
            variant="outline"
            leftIcon={<AddIcon />}
            onClick={onOpen}
          >
            {formatMessage({ id: 'pmportal.connections.addCode' })}
          </Button>
        }
      >
        <ConnectionsTable connections={data?.results} onRemove={handleRemove} />
        <Pagination
          rowsPerPage={rowsPerPage}
          hasNext={data?.hasNextPage}
          hasPrev={data?.hasPreviousPage}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          onPrev={() =>
            setPaginationTokenOption({
              previousPageToken: data?.paginationToken,
            })
          }
          onNext={() =>
            setPaginationTokenOption({ nextPageToken: data?.paginationToken })
          }
        />
      </EditableSection>

      <ConnectionAccessCodeModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={value =>
          accept.mutateAsync(
            { organizationId, workspaceId, body: { accessCode: value } },
            {
              onSuccess: () => {
                addMessage({
                  status: 'success',
                  description: formatMessage({
                    id: 'pmportal.connections.connectSuccess',
                  }),
                })
              },
            }
          )
        }
      />
    </FullWidthPage>
  )
}

export default Connections
